import { ActionSheet } from "@capacitor/action-sheet";
import { AppLauncher } from "@capacitor/app-launcher";
import { Capacitor } from "@capacitor/core";
import { type CSSObject } from "@emotion/react";
import { IonText } from "@ionic/react";
import { useHistory } from "react-router";
import { Colors, Icon } from "swing-components";

import { ButtonScore, Card, cardHeaderData, Chip } from "~components";
import { createMyRequestsDetailsUrl } from "~pages";
import {
  formatCityStateZip,
  formatDisplayTimes,
  formatPay,
  generateGoogleMapsLink,
  getBonusDetails,
  handleMapLinkNavigation,
  openLinkInApp,
  PastCommitmentRequestData,
  translate,
  UpcomingCommitmentRequestData,
} from "~utils";

export type CommitmentCardProps = {
  request: UpcomingCommitmentRequestData | PastCommitmentRequestData;
};

export function CommitmentCard(props: CommitmentCardProps) {
  const { request } = props;
  const chipData = cardHeaderData[request.status];

  const requestUrl = createMyRequestsDetailsUrl(request.id);
  const isPast = request.status === "STATUS_COMPLETED";

  const history = useHistory();
  const hasFeedback = "hasFeedback" in request ? request.hasFeedback : false;
  const { hasRequestBonus, bonusHourlyPay } = getBonusDetails(request.payment);

  return (
    <Card href={requestUrl}>
      <div css={{ minHeight: "fit-content" }}>
        <Chip color={chipData.chipColor} label={chipData.chipLabel} icon={chipData.icon} />
        <div
          css={{
            display: "grid",
            marginTop: 16,
          }}
        >
          <span css={{ fontWeight: "var(--swing-font-semibold)" }}>{request.displayDate}</span>
          {!request.isMultiDay && (
            <span css={{ color: Colors.black, fontSize: 12 }}>
              {formatDisplayTimes(request.displayTime)}
            </span>
          )}
          {!!("hasConflict" in request && request.hasConflict) && (
            <span css={{ color: Colors.red500, fontSize: 12 }}>
              {translate("COMMITMENT_CARD_CONFLICT")}
            </span>
          )}
        </div>
        <div
          css={{
            width: "100%",
            height: 1,
            margin: "8px 0",
            paddingRight: 16,
            backgroundColor: Colors.slate300,
          }}
        />
        <div>
          <div css={STYLES.infoRow}>
            <div>
              {request.school.name}
              {!isPast && request.school.address && (
                <a
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    zIndex: 2,
                    fontSize: 12,
                  }}
                  target="_blank"
                  rel="noreferrer noopener"
                  href={generateGoogleMapsLink(request.school.address)}
                  onClick={(e) =>
                    handleMapLinkNavigation(e, {
                      getPlatform: Capacitor.getPlatform,
                      appLauncher: AppLauncher,
                      actionSheet: ActionSheet,
                      openLinkInApp: openLinkInApp,
                      query: encodeURIComponent(
                        `${request.school.address?.street} ${request.school.address?.city}, ${request.school.address?.state}, ${request.school.address?.zip}`,
                      ),
                      link: generateGoogleMapsLink({ ...request.school.address }),
                    })
                  }
                >
                  {request.school.address.street && (
                    <IonText css={STYLES.truncateOneLine}>{request.school.address.street}</IonText>
                  )}
                  <IonText css={STYLES.truncateOneLine}>
                    {formatCityStateZip(request.school.address)}
                  </IonText>
                </a>
              )}
            </div>
          </div>
          {request.payment && (
            <PayDisplay payment={{ ...request.payment, bonusHourlyPay, hasRequestBonus }} />
          )}
          {isPast && (
            <div css={{ display: "flex", justifyContent: "center" }}>
              {!hasFeedback ? (
                <ButtonScore
                  onClick={() => {
                    history.push(requestUrl);
                  }}
                  fill="outline"
                  label={translate("SCHOOL_FEEDBACK_TITLE")}
                />
              ) : (
                <span
                  css={{
                    fontWeight: "var(--swing-font-semibold)",
                    color: "var(--swing-green500)",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <Icon name="Check Circle" color={Colors.green500}></Icon>
                  {translate("SCHOOL_FEEDBACK_REVIEWED_LABEL")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

type PayDisplayProps = {
  payment?: (UpcomingCommitmentRequestData["payment"] | PastCommitmentRequestData["payment"]) & {
    bonusHourlyPay?: number;
    hasRequestBonus?: boolean;
  };
};

function PayDisplay(props: PayDisplayProps) {
  const { payment } = props;

  if (!payment) {
    return null;
  }

  if (payment.isHourly) {
    return (
      <div css={{ ...STYLES.infoRow, display: "flex", gap: 0, flexDirection: "column" }}>
        {payment?.totalPayValue && payment.totalPayValue > 0 ? (
          <p css={STYLES.payDisplay}>
            {formatPay(false, payment.totalPayValue)} {translate("LABEL_ESTIMATED_TOTAL_SHORT")}
          </p>
        ) : null}
        {payment?.hourlyPayValue && payment.hourlyPayValue > 0 && (
          <>
            <p css={{ ...STYLES.payDisplay, fontSize: 12, fontWeight: 400 }}>
              {formatPay(
                true,
                payment.bonusHourlyPay ? payment.bonusHourlyPay : payment.hourlyPayValue,
              )}
            </p>
            {payment.hasRequestBonus && payment.bonusHourlyPay && (
              <p css={{ ...STYLES.payDisplay, fontSize: 11, fontWeight: 200 }}>
                <s>{formatPay(true, payment.hourlyPayValue)}</s>
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div css={STYLES.infoRow}>
      <p css={STYLES.payDisplay}>
        {formatPay(payment.isHourly, payment.payValue)}{" "}
        {!payment.isHourly && translate("OPENINGS_CARD_PAY_NON_HOURLY")}
      </p>
    </div>
  );
}

const STYLES = {
  payDisplay: {
    margin: 0,
    color: Colors.green500,
    fontSize: 14,
    fontWeight: 600,
  },
  infoRow: {
    display: "flex",
    gap: 16,
    margin: "8px auto",
  },
  truncateOneLine: {
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    display: "webkit-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
} satisfies Record<string, CSSObject>;
